import React, { useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { DownloadIcon, ShieldCheckIcon, LockIcon, FilterIcon } from 'lucide-react';
const sampleData = [{
  domain: 'kith.com',
  owner: 'Ronnie Fieg',
  email: 'ronnie@kith.com',
  platform: 'Shopify',
  category: 'Apparel',
  avgTicket: '$185',
  revenue: '$100M+'
}, {
  domain: 'allbirds.com',
  owner: 'Tim Brown',
  email: 'tim@allbirds.com',
  platform: 'Shopify',
  category: 'Apparel',
  avgTicket: '$110',
  revenue: '$250M+'
}, {
  domain: 'rothys.com',
  owner: 'Stephen Hawthornthwaite',
  email: 'stephen@rothys.com',
  platform: 'Shopify',
  category: 'Apparel',
  avgTicket: '$145',
  revenue: '$140M+'
}, {
  domain: 'gymshark.com',
  owner: 'Ben Francis',
  email: 'ben@gymshark.com',
  platform: 'Shopify',
  category: 'Apparel',
  avgTicket: '$72',
  revenue: '$500M+'
}, {
  domain: 'colourpop.com',
  owner: 'Laura Nelson',
  email: 'laura@colourpop.com',
  platform: 'Shopify',
  category: 'Beauty',
  avgTicket: '$28',
  revenue: '$200M+'
}, {
  domain: 'casper.com',
  owner: 'Philip Krim',
  email: 'philip@casper.com',
  platform: 'Shopify',
  category: 'Home & Garden',
  avgTicket: '$950',
  revenue: '$450M+'
}, {
  domain: 'blendjet.com',
  owner: 'Ryan Pamplin',
  email: 'ryan@blendjet.com',
  platform: 'Shopify',
  category: 'Home & Garden',
  avgTicket: '$55',
  revenue: '$80M+'
}, {
  domain: 'chubbiesshorts.com',
  owner: 'Kyle Hency',
  email: 'kyle@chubbiesshorts.com',
  platform: 'Shopify',
  category: 'Apparel',
  avgTicket: '$68',
  revenue: '$50M+'
}, {
  domain: 'puravidabracelets.com',
  owner: 'Griffin Thall',
  email: 'griffin@puravidabracelets.com',
  platform: 'Shopify',
  category: 'Apparel',
  avgTicket: '$35',
  revenue: '$75M+'
}, {
  domain: 'deathwishcoffee.com',
  owner: 'Mike Brown',
  email: 'mike@deathwishcoffee.com',
  platform: 'Shopify',
  category: 'Food & Beverage',
  avgTicket: '$22',
  revenue: '$15M+'
}, {
  domain: 'fashionnova.com',
  owner: 'Richard Saghian',
  email: 'richard@fashionnova.com',
  platform: 'Shopify',
  category: 'Apparel',
  avgTicket: '$45',
  revenue: '$1B+'
}, {
  domain: 'brooklinen.com',
  owner: 'Rich Fulop',
  email: 'rich@brooklinen.com',
  platform: 'Shopify',
  category: 'Home & Garden',
  avgTicket: '$120',
  revenue: '$100M+'
}, {
  domain: 'decathlon.com',
  owner: 'Michel Aballea',
  email: 'michel@decathlon.com',
  platform: 'Magento',
  category: 'Apparel',
  avgTicket: '$85',
  revenue: '$15B+'
}, {
  domain: 'skullcandy.com',
  owner: 'Jason Hodell',
  email: 'jason@skullcandy.com',
  platform: 'BigCommerce',
  category: 'Electronics',
  avgTicket: '$65',
  revenue: '$300M+'
}, {
  domain: 'kyliecosmetics.com',
  owner: 'Kylie Jenner',
  email: 'kylie@kyliecosmetics.com',
  platform: 'Shopify',
  category: 'Beauty',
  avgTicket: '$42',
  revenue: '$600M+'
}];
export function EcommerceDataPreview() {
  const [activeRow, setActiveRow] = useState<number | null>(null);
  const [platformFilter, setPlatformFilter] = useState('All');
  const [categoryFilter, setCategoryFilter] = useState('All');
  const filteredData = useMemo(() => {
    return sampleData.filter(item => {
      if (platformFilter !== 'All' && item.platform !== platformFilter) return false;
      if (categoryFilter !== 'All' && item.category !== categoryFilter) return false;
      return true;
    });
  }, [platformFilter, categoryFilter]);
  return <section className="py-24 bg-white" data-id="element-818">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-819">
        <div className="text-center max-w-3xl mx-auto mb-12" data-id="element-820">
          <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4 leading-tight" data-id="element-821">
            Preview the e-commerce database
          </h2>
          <p className="text-xl text-slate-600" data-id="element-822">
            Filter by platform and category. Our e-commerce listings include
            verified store owner emails, average ticket size, and technographic
            data for online stores.
          </p>
        </div>

        <div className="bg-white rounded-2xl border border-slate-200 shadow-xl overflow-hidden" data-id="element-823">
          <div className="p-6 border-b border-slate-200 bg-slate-50" data-id="element-824">
            <div className="flex flex-wrap items-center gap-3" data-id="element-825">
              <div className="flex items-center gap-2 mr-auto" data-id="element-826">
                <FilterIcon className="w-5 h-5 text-slate-400" data-id="element-827" />
                <h3 className="text-lg font-bold text-slate-900 whitespace-nowrap" data-id="element-828">
                  Filter e-commerce stores
                </h3>
              </div>

              <select className="bg-white border border-slate-300 text-slate-700 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block p-2.5 shadow-sm" value={platformFilter} onChange={e => setPlatformFilter(e.target.value)} data-id="element-829">
                <option value="All" data-id="element-830">All Platforms</option>
                <option value="Shopify" data-id="element-831">Shopify</option>
                <option value="WooCommerce" data-id="element-832">WooCommerce</option>
                <option value="BigCommerce" data-id="element-833">BigCommerce</option>
                <option value="Magento" data-id="element-834">Magento</option>
                <option value="Wix" data-id="element-835">Wix</option>
              </select>

              <select className="bg-white border border-slate-300 text-slate-700 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block p-2.5 shadow-sm" value={categoryFilter} onChange={e => setCategoryFilter(e.target.value)} data-id="element-836">
                <option value="All" data-id="element-837">All Categories</option>
                <option value="Apparel" data-id="element-838">Apparel</option>
                <option value="Electronics" data-id="element-839">Electronics</option>
                <option value="Food & Beverage" data-id="element-840">Food & Beverage</option>
                <option value="Beauty" data-id="element-841">Beauty</option>
                <option value="Home & Garden" data-id="element-842">Home & Garden</option>
              </select>

              {(platformFilter !== 'All' || categoryFilter !== 'All') && <button onClick={() => {
              setPlatformFilter('All');
              setCategoryFilter('All');
            }} className="text-xs font-medium text-slate-500 hover:text-slate-700 transition-colors underline underline-offset-2" data-id="element-843">
                  Clear all
                </button>}
            </div>
          </div>

          <div className="overflow-x-auto" data-id="element-844">
            <table className="w-full text-sm text-left" data-id="element-845">
              <thead data-id="element-846">
                <tr className="text-slate-500 font-semibold border-b border-slate-200 bg-slate-50/50" data-id="element-847">
                  <th className="py-4 px-4 whitespace-nowrap" data-id="element-848">Store domain</th>
                  <th className="py-4 px-4 whitespace-nowrap" data-id="element-849">
                    Verified owner / contact
                  </th>
                  <th className="py-4 px-4 whitespace-nowrap" data-id="element-850">
                    <div className="flex items-center gap-2" data-id="element-851">
                      Email
                      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-700" data-id="element-852">
                        Verified
                      </span>
                    </div>
                  </th>
                  <th className="py-4 px-4 whitespace-nowrap" data-id="element-853">Platform</th>
                  <th className="py-4 px-4 whitespace-nowrap" data-id="element-854">Category</th>
                  <th className="py-4 px-4 whitespace-nowrap" data-id="element-855">Avg. ticket</th>
                  <th className="py-4 px-4 whitespace-nowrap" data-id="element-856">Est. revenue</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100" data-id="element-857">
                {filteredData.length > 0 ? filteredData.map((row, idx) => {
                const isBlurred = idx >= 3;
                return <motion.tr key={idx} onHoverStart={() => !isBlurred && setActiveRow(idx)} onHoverEnd={() => !isBlurred && setActiveRow(null)} className={`transition-all duration-200 ${isBlurred ? 'bg-slate-50/50 select-none' : 'cursor-pointer'} ${activeRow === idx && !isBlurred ? 'bg-blue-50' : !isBlurred ? 'hover:bg-slate-50' : ''}`} data-id="element-858">
                        <td className="py-4 px-4 font-semibold text-slate-900 whitespace-nowrap" data-id="element-859">
                          {row.domain}
                        </td>
                        <td className="py-4 px-4 text-slate-600 whitespace-nowrap" data-id="element-860">
                          {row.owner}
                        </td>
                        <td className="py-4 px-4 whitespace-nowrap relative" data-id="element-861">
                          {isBlurred ? <div className="flex items-center justify-center absolute inset-0 bg-white/40 backdrop-blur-[4px] z-10" data-id="element-862">
                              <LockIcon className="w-4 h-4 text-slate-400" data-id="element-863" />
                            </div> : null}
                          <div className={`flex items-center gap-1.5 ${isBlurred ? 'opacity-30' : ''}`} data-id="element-864">
                            <ShieldCheckIcon className="w-3.5 h-3.5 text-green-600 flex-shrink-0" data-id="element-865" />
                            <span className="text-blue-600 font-medium" data-id="element-866">
                              {row.email}
                            </span>
                          </div>
                        </td>
                        <td className="py-4 px-4 text-slate-600 whitespace-nowrap" data-id="element-867">
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-slate-100 text-slate-800" data-id="element-868">
                            {row.platform}
                          </span>
                        </td>
                        <td className="py-4 px-4 text-slate-600 whitespace-nowrap" data-id="element-869">
                          {row.category}
                        </td>
                        <td className="py-4 px-4 text-slate-700 whitespace-nowrap font-medium" data-id="element-870">
                          {row.avgTicket}
                        </td>
                        <td className="py-4 px-4 text-slate-700 whitespace-nowrap font-semibold" data-id="element-871">
                          {row.revenue}
                        </td>
                      </motion.tr>;
              }) : <tr data-id="element-872">
                    <td colSpan={7} className="py-12 text-center text-slate-500" data-id="element-873">
                      No e-commerce stores found matching your filters. Try
                      adjusting your criteria.
                    </td>
                  </tr>}
              </tbody>
            </table>
          </div>

          <div className="p-4 bg-slate-50 border-t border-slate-200 flex flex-col sm:flex-row items-center justify-between gap-4" data-id="element-874">
            <p className="text-sm text-slate-500" data-id="element-875">
              Showing {filteredData.length} of{' '}
              <strong className="text-slate-900" data-id="element-876">14M+</strong> verified
              e-commerce store records.
            </p>
            <a href="http://app.openmart.com/register" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 px-5 py-2.5 bg-blue-600 text-white text-sm font-semibold rounded-lg hover:bg-blue-700 transition-colors" data-id="element-877">
              <DownloadIcon className="w-4 h-4" data-id="element-878" />
              Unlock full database
            </a>
          </div>
        </div>
      </div>
    </section>;
}